import { Duration } from 'luxon';

import { getTenantUnits } from '@core/configuration';
import { ProductsByPlantFormikType, ProjectFormikType } from '@core/formik/project';
import { ProjectOrQuoteProductFormikType } from '@core/formik/projectOrQuoteProduct';
import { ProjectStatus } from '@core/types/api/ProjectStatus/ProjectStatus';

import { Currency } from '../types/api/Currency/Currency';
import Enums from '../types/api/Enums';
import type { ProjectPlant } from '../types/api/ProjectPlant/ProjectPlant';
import type { ProjectProduct } from '../types/api/ProjectProduct/ProjectProduct';
import { hasCompetitorLossDataFromFormik } from './competitors';
import { roundDurationToMinutes } from './dateHelpers';
import { formatQuantityWithUnit } from './decimals';
import { hasValue } from './equality';
import { formatCurrency, resolveCurrencyCode } from './format';
import { logger } from './logger';
import { sumCurrency, sumNumber } from './plantCalculations';
import { getUnitLabel, convertBetweenLengthUnits } from './unitHelpers';

export const mergeProductsByPlantsFormik = (
  arr1: ProductsByPlantFormikType[],
  arr2: ProductsByPlantFormikType[],
): ProductsByPlantFormikType[] => {
  const map = new Map<string, ProductsByPlantFormikType>();

  const addPlant = (plant: ProductsByPlantFormikType): void => {
    const plantId = plant.plant.id;
    if (!plantId || !map.has(plantId)) {
      // Just add the new plant
      if (plantId) {
        map.set(plantId, { ...plant, products: [...plant.products] });
      }
      return;
    }

    // Merge with the existing plant
    const existing = map.get(plantId)!;

    // Create a map by product.id
    const productsMap = new Map<string, ProjectOrQuoteProductFormikType>();
    for (const p of existing.products) {
      if (p.product.id) {
        productsMap.set(p.product.id, p);
      }
    }

    // Add the new products
    for (const prod of plant.products) {
      if (prod.product.id) {
        productsMap.set(prod.product.id, prod);
      }
    }

    existing.products = [...productsMap.values()];
  };

  // Process both arrays
  arr1.forEach(addPlant);
  arr2.forEach(addPlant);

  return [...map.values()];
};

export type DistanceInfo = {
  durationText: string;
  distanceText: string;
};

export const getPlantDistanceInfo = (plantDistanceInfo: {
  plantDriveTime: string | null;
  plantDistance: string | null;
  plantDistanceUnit: Enums.Unit | null;
}): DistanceInfo | null => {
  if (!hasValue(plantDistanceInfo.plantDriveTime) || !hasValue(plantDistanceInfo.plantDistance)) {
    return null;
  }

  const driveTime = Duration.fromISO(plantDistanceInfo.plantDriveTime);
  let distanceValue = parseFloat(plantDistanceInfo.plantDistance);
  let currentUnit = plantDistanceInfo.plantDistanceUnit;

  const tenantConfig = getTenantUnits();
  const tenantUnit = tenantConfig.length;
  const tenantLocale = tenantConfig.locale;

  // Convert the distance to the tenant's preferred length unit if it differs from the plant's unit
  if (currentUnit && tenantUnit && currentUnit !== tenantUnit) {
    try {
      distanceValue = convertBetweenLengthUnits(distanceValue, currentUnit, tenantUnit);
      currentUnit = tenantUnit;
    } catch (error) {
      logger.error('Error converting distance units:', error);
    }
  }

  // Automatically scale large metric distances to kilometers for better UI readability
  if (currentUnit === Enums.Unit.M && distanceValue >= 1000) {
    try {
      distanceValue = convertBetweenLengthUnits(distanceValue, Enums.Unit.M, Enums.Unit.Km);
      currentUnit = Enums.Unit.Km;
    } catch (error) {
      logger.error('Error converting distance units:', error);
    }
  }

  // A plant is genuinely unreachable only when there is no route at all: both the
  // distance and the drive time are zero. A very close plant can round to a zero
  // distance (e.g. 0.2 mi -> 0 after Distance.toUnit's Math.round) while still having
  // a real drive time, and must not be treated as unreachable.
  if (distanceValue === 0 && (!driveTime.isValid || driveTime.toMillis() === 0)) {
    return { distanceText: 'Unreachable', durationText: 'Unreachable' };
  }
  if (!driveTime.isValid) {
    return null;
  }

  const unitLabel = currentUnit ? getUnitLabel(currentUnit) : getUnitLabel(tenantUnit);

  const formattedDistance = distanceValue.toLocaleString(tenantLocale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  // When a reachable plant rounds down to a zero distance, surface that it is closer
  // than one unit rather than showing a misleading "0 mi".
  const distanceText =
    distanceValue === 0 ? `< 1 ${unitLabel}` : `${formattedDistance} ${unitLabel}`;

  // Round to the same whole minute the delivery cost is calculated from and the
  // database stores, so the drive time shown here explains the cost shown next to it.
  const totalMinutes = roundDurationToMinutes(driveTime).as('minutes');
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let durationText = '';

  if (hours > 0) {
    durationText = `${hours} h ${minutes} min`;
  } else if (minutes > 0) {
    durationText = `${minutes} min`;
  }

  return {
    durationText,
    distanceText,
  };
};

/**
 * Statistics for a plant location including revenue, costs, margins, and quantities.
 */
export type PlantStats = {
  distance: string | undefined;
  estReadyMixQuantity: string;
  estRevenue: string;
  estCosts: string;
  pricePerYard: string;
  estMargin: string;
};

/**
 * A plant with its associated products.
 */
export type PlantWithProducts = ProjectPlant & { products: ProjectProduct[] };

/**
 * Calculates aggregated statistics for a plant and its products.
 *
 * Logic:
 * - **Unit Cost**: Sum of `materialCost` and `otherCost`.
 * - **Revenue**: Sum of `unitPrice` * `quantity`.
 * - **Volume**: Sum of `quantity` for 'Mix' products.
 * - **Price Per Yard**: Uses Mix Revenue / Mix Volume.
 *
 * @param plant - The plant with its associated products
 * @returns Calculated statistics for the plant
 */
export function calculatePlantStats(plant: PlantWithProducts): PlantStats {
  const mappedProducts = plant.products.map((product) => {
    const quantity = parseFloat(product.quantity || '0');
    const unitCost =
      product.materialCost.add(product.calculateOtherCost() ?? Currency.zero()) ?? Currency.zero();
    const unitPrice = product.price;

    return {
      type: product.product.category,
      quantity,
      unitCost,
      unitPrice,
      totalCost: unitCost.multiply(quantity) ?? Currency.zero(),
      totalRevenue: unitPrice.multiply(quantity) ?? Currency.zero(),
    };
  });

  const mixProducts = mappedProducts.filter(
    (product) => product.type === Enums.ProductCategory.ReadyMix,
  );
  const mixRevenueValue = sumCurrency(mixProducts, (p) => p.totalRevenue);

  const unitLabel = plant.plantDistanceUnit
    ? getUnitLabel(plant.plantDistanceUnit)
    : getTenantUnits().length;
  const distanceFromLocation = plant.plantDistance
    ? `${Number(plant.plantDistance)} ${unitLabel}`
    : undefined;

  const estRevenueValue = sumCurrency(mappedProducts, (p) => p.totalRevenue);
  const estCostsValue = sumCurrency(mappedProducts, (product) => product.totalCost);

  const estimatedReadyMixQuantity = sumNumber(mixProducts, (product) => product.quantity);

  let pricePerYardValue = Currency.zero();
  if (estimatedReadyMixQuantity > 0) {
    pricePerYardValue = mixRevenueValue.divide(estimatedReadyMixQuantity) ?? Currency.zero();
  }

  const currencyCode = resolveCurrencyCode(plant.plant.currencyCode) as Enums.CurrencyCode;

  return {
    distance: distanceFromLocation,
    estRevenue: formatCurrency(estRevenueValue.toUnit(), currencyCode),
    estCosts: formatCurrency(estCostsValue.toUnit(), currencyCode),
    estMargin: formatCurrency(
      (estRevenueValue.subtract(estCostsValue) ?? Currency.zero()).toUnit(),
      currencyCode,
    ),
    estReadyMixQuantity: formatQuantityWithUnit(
      String(estimatedReadyMixQuantity),
      getTenantUnits().dryVolume,
    ),
    pricePerYard: formatCurrency(pricePerYardValue.toUnit(), currencyCode),
  };
}

export const isArchiveProjectStatus = (status: ProjectStatus): boolean =>
  status.systemBehavior === Enums.ProjectStatusSystemBehavior.Archive || status.isArchived;

export const isLostProjectStatus = (status: ProjectStatus): boolean =>
  status.systemBehavior === Enums.ProjectStatusSystemBehavior.Lost;

export const getIsShowCompetitorInfoSectionFromProjectFormik = (
  statusList: ProjectStatus[] | undefined,
  formikBagValues: ProjectFormikType,
): boolean => {
  const currentStatus = statusList?.find((s) => s.id === formikBagValues.projectStatus.id) ?? null;
  const isLost = currentStatus !== null && isLostProjectStatus(currentStatus);
  const isArchived = currentStatus !== null && isArchiveProjectStatus(currentStatus);
  return isLost || (isArchived && hasCompetitorLossDataFromFormik(formikBagValues));
};
